import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import Button from '../common/Button';
import Input from '../common/Input';
import { subscribePrelaunch } from '../../services/auth';
import { getErrorMessage } from '../../services/api';
import PublicHeader from './PublicHeader';
import PublicFooter from './PublicFooter';
import { useRegistrationEnabled } from '../../hooks/usePublicConfig';

interface Breadcrumb {
  label: string;
  href?: string;
}

interface FeatureLayoutProps {
  children: React.ReactNode;
  breadcrumbs: Breadcrumb[];
}

export default function FeatureLayout({ children, breadcrumbs }: FeatureLayoutProps) {
  const { t } = useTranslation('features');
  const { t: tc } = useTranslation('common');
  const registrationEnabled = useRegistrationEnabled();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      await subscribePrelaunch(email);
      setIsSuccess(true);
      setEmail('');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Accueil',
        item: 'https://splinity.com/',
      },
      ...breadcrumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 2,
        name: crumb.label,
        ...(crumb.href ? { item: `https://splinity.com${crumb.href}` } : {}),
      })),
    ],
  };

  return (
    <div className="pub-page min-h-screen flex flex-col">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>
      <PublicHeader />

      {/* Breadcrumb */}
      <div className="pub-section-alt pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/" className="pub-subtle hover:text-primary-600 transition-colors">
              {tc('nav.home')}
            </Link>
            {breadcrumbs.map((crumb, index) => (
              <span key={index} className="flex items-center gap-2">
                <ChevronRight className="w-4 h-4 pub-subtle" />
                {crumb.href ? (
                  <Link to={crumb.href} className="pub-subtle hover:text-primary-600 transition-colors">
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="font-medium">{crumb.label}</span>
                )}
              </span>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* CTA final */}
      <section className="pub-cta py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="pub-title text-2xl md:text-3xl font-bold mb-4">
            {t('ctaTitle')}
          </h2>
          <p className="text-lg opacity-90 mb-8">
            {t('ctaDescription')}
          </p>

          {registrationEnabled === true ? (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/register">
                <Button
                  variant="outline"
                  size="lg"
                  className="!bg-white !text-primary-700 !border-white hover:!bg-white/90"
                  rightIcon={<ArrowRight className="w-4 h-4" />}
                >
                  {t('createAccountFree')}
                </Button>
              </Link>
              <Link to="/login">
                <Button
                  variant="outline"
                  size="lg"
                  className="!border-white/50 !text-white hover:!bg-white/10"
                >
                  {t('signIn')}
                </Button>
              </Link>
            </div>
          ) : registrationEnabled === false ? (
            <div className="max-w-md mx-auto">
              {isSuccess ? (
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <Check className="w-8 h-8 mx-auto mb-3" />
                  <p className="font-semibold mb-1">{t('thankYou')}</p>
                  <p className="text-sm opacity-80">
                    {t('willContact')}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3">
                  <Input
                    type="email"
                    placeholder={t('emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={error}
                    required
                    className="!bg-white/10 !border-white/30 !text-white !placeholder-white/60"
                  />
                  <Button
                    type="submit"
                    variant="outline"
                    className="!bg-white !text-primary-700 !border-white hover:!bg-white/90 whitespace-nowrap"
                    isLoading={isSubmitting}
                  >
                    {t('subscribe')}
                  </Button>
                </form>
              )}
            </div>
          ) : null}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
